import React, { useEffect, useState } from "react";
import { FaArrowLeft, FaBook, FaGraduationCap, FaClock, FaQuestionCircle, FaUser, FaCheckCircle, FaListUl, FaPlay, FaPencilAlt } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../../config/apiConfig";
import { useQuiz } from "../../utils/api/Redux/QuizContext";
import { ClipLoader } from "react-spinners";
import toast, { Toaster } from "react-hot-toast";

const parseSubjects = (data) => {
    // 1. If it's already an array, return it immediately
    if (Array.isArray(data)) return data;

    // 2. If it's a string, try to parse it
    if (typeof data === 'string') {
        try {
            const parsed = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return []; // Fallback for malformed strings
        }
    }

    // 3. Fallback for null, undefined, or unexpected types
    return [];
};

const QUESTIONS_CONFIG = {
    jamb: {
        English: 60,
        default: 40,
    },
    waec: {
        English: 50,
        default: 50,
    },
};


const MINUTES_PER_SUBJECT = 30;


const TakeJAMBOrWAECSession = () => {
    const navigate = useNavigate();
    const { examType } = useParams(); // 'jamb' or 'waec'
    const { setQuizData } = useQuiz();

    // JAMB registration is a fixed 4 subjects; WAEC allows up to 9
    // (2 compulsory + up to 7 electives).
    const maxSubjects = examType?.toLowerCase() === "waec" ? 9 : 4;

    const [userData, setUserData] = useState(null);
    const [quizData, setQuizBank] = useState([]);
    const [selectedSubjects, setSelectedSubjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch user profile
    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/users/me`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                });
                if (!response.ok) throw new Error("Failed to fetch user profile");

                const user = await response.json();
                setUserData(user);
            } catch (error) {
                console.error("Error fetching profile:", error.message);
            }
        };

        fetchProfile();
    }, []);

    // Fetch all questions
    useEffect(() => {
        const fetchQuestions = async () => {
            setIsLoading(true);
            const TOAST_ID = "quiz-fetch-error";

            try {
                const response = await fetch(
                    `${API_BASE_URL}/api/questions/exam/${examType.toLowerCase()}`,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    }
                );

                const data = await response.json();
                // console.log("Fetched questions:", data);

                if (!response.ok) {
                    const errorMsg = data?.message || "Failed to fetch quiz questions.";
                    toast.error(errorMsg, { id: TOAST_ID });
                    throw new Error(errorMsg);
                }

                // Only filter by user's subjects
                const subjects = parseSubjects(userData?.subjects || []);

                const filtered = data.filter((q) => subjects.includes(q.subject));
                console.log("Filtered questions:", filtered);

                setQuizBank(filtered || []);

            } catch (error) {
                console.error("Error fetching questions:", error.message);
                toast.error(error.message || "Something went wrong while loading questions.", {
                    id: TOAST_ID,
                });
            } finally {
                setIsLoading(false);
            }
        };

        if (userData) {
            fetchQuestions();
        }
    }, [userData, examType]);

    const subjectQuestionCount = quizData.reduce((acc, q) => {
        acc[q.subject] = (acc[q.subject] || 0) + 1;
        return acc;
    }, {});



    // Subjects allowed for this user
    const userSubjects = userData ? parseSubjects(userData.subjects) : [];

    // Handle subject selection (max 4)
    const handleSubjectToggle = (subject) => {
        if (selectedSubjects.includes(subject)) {
            setSelectedSubjects(selectedSubjects.filter(s => s !== subject));
        } else {
            if (selectedSubjects.length < maxSubjects) {
                setSelectedSubjects([...selectedSubjects, subject]);
            } else {
                toast.error(`You can only select ${maxSubjects} subjects`, { id: "max-subjects" });
            }
        }
    };
    console.log(quizData)
    // Get questions for selected subjects
    const getFilteredQuestions = () => {
        return quizData.filter((q) => selectedSubjects.includes(q.subject));
    };


    // Create new test
    const createTest = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/api/tests-promo`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to create test");
            }

            const test = await response.json();
            localStorage.setItem("test_id", test.data.id);
            return test.data.id;
        } catch (error) {
            console.error("Error creating test:", error.message);
            return null;
        }
    };

    // Start Exam
    const startExam = async () => {
        if (selectedSubjects.length < 1) {
            toast.error("Please select exactly 4 subjects", {
                id: "select-4",
            });
            return;
        }

        const selectedQuestions = getFilteredQuestions();

        if (selectedQuestions.length === 0) {
            toast.error("No questions available for the selected subjects.", {
                id: "no-questions",
            });
            return;
        }

        // Build final question set
        let finalQuestions = [];

        selectedSubjects.forEach(subject => {
            const limit =
                subject.toLowerCase() === "english"
                    ? QUESTIONS_CONFIG[examType.toLowerCase()].English
                    : QUESTIONS_CONFIG[examType.toLowerCase()].default;


            const subjectQuestions = selectedQuestions
                .filter(q => q.subject === subject)
                .sort(() => Math.random() - 0.5)
                .slice(0, limit);

            finalQuestions.push(...subjectQuestions);
        });

        // Save questions globally
        setQuizData(finalQuestions);

        // 30 minutes per subject
        const totalTimeInSeconds =
            selectedSubjects.length * MINUTES_PER_SUBJECT * 60;

        // Create test
        const testId = await createTest();
        if (!testId) {
            toast.error("Could not create test. Please try again.", {
                id: "create-test-error",
            });
            return;
        }

        // Navigate to full exam page
        navigate("/exam-promo", {
            state: {
                test_id: testId,
                time_left: totalTimeInSeconds,
                examType,
                mode: "exam",
                subjects: selectedSubjects,
                yearGroup: userData?.yearGroup,
            },
        });
    };


    // Get exam info
    const getExamInfo = () => {
        if (examType === "JAMB") {
            return {
                icon: <FaGraduationCap className="text-3xl" />,
                title: "JAMB UTME Examination",
                description: "4 subjects • 160 questions • 3 hours",
                color: "text-green-600",
                bgColor: "bg-green-50",
                borderColor: "border-green-200"
            };
        } else {
            return {
                icon: <FaGraduationCap className="text-3xl" />,
                title: "WAEC Examination",
                description: "4 subjects • 200 questions • 3 hours",
                color: "text-blue-600",
                bgColor: "bg-blue-50",
                borderColor: "border-blue-200"
            };
        }
    };

    const examInfo = getExamInfo();

    if (isLoading || !userData) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-100">
                <div className="text-center">
                    <ClipLoader color="#2148C0" size={50} />
                    <p className="ml-3 text-[#2148C0] font-medium mt-4">Loading your exam page...</p>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Back Button */}
            <div className="flex items-center mb-2 mt-6">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center ml-[30px] mt-8 gap-2 text-[#2148C0] hover:text-blue-600 font-medium transition-all duration-200 hover:gap-3"
                >
                    <FaArrowLeft className="text-xl" />
                    Back
                </button>
            </div>
            <Toaster position="top-right" reverseOrder={false} />

            {/* Main Content */}
            <div className="min-h-screen bg-gray-50 py-10 px-6">
                <div className="max-w-5xl mx-auto bg-white p-8 rounded-xl shadow-lg">
                    {/* Header with Exam Info */}
                    <div className={`${examInfo.bgColor} ${examInfo.borderColor} border-2 rounded-xl p-6 mb-8`}>
                        <div className="flex items-center justify-center gap-4 mb-4">
                            <div className={examInfo.color}>
                                {examInfo.icon}
                            </div>
                            <div className="text-center">
                                <h1 className={`text-3xl font-bold ${examInfo.color} mb-2`}>
                                    {examInfo.title}
                                </h1>
                                {/* <p className="text-gray-600 flex items-center justify-center gap-2">
                                    <FaClock className="text-sm" />
                                    {examInfo.description}
                                </p> */}
                            </div>
                        </div>

                        {/* User Info */}
                        <div className="flex flex-col sm:flex-row sm:justify-center sm:items-center gap-4 sm:gap-6 text-sm text-gray-600 border-t pt-4">
                            <div className="flex items-center gap-2">
                                <FaUser className="text-blue-500" />
                                <span>{userData?.firstName} {userData?.lastName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaGraduationCap className="text-green-500" />
                                <span>{userData?.yearGroup}</span>
                            </div>
                        </div>
                    </div>

                    {/* Subject Selection Instructions */}
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-3">
                            <FaQuestionCircle className="text-yellow-600 text-xl mt-1" />
                            <div>
                                <h3 className="font-semibold text-yellow-800 mb-1">
                                    Select Your {maxSubjects} Subjects
                                </h3>
                                <p className="text-sm text-yellow-700">
                                    Choose at least 1 subject from your available subjects to take the {examType.toUpperCase()} examination.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Subject Selection Counter */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <FaBook className="text-2xl text-[#2148C0]" />
                                <div>
                                    <h2 className="text-xl font-semibold text-gray-700">
                                        Select Subjects
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        {userSubjects.length} subjects available
                                    </p>
                                </div>
                            </div>
                            <div className={`px-4 py-2 rounded-lg font-bold ${selectedSubjects.length === maxSubjects
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                                }`}>
                                {selectedSubjects.length} / {maxSubjects} Selected
                            </div>
                        </div>

                        {userSubjects.length === 0 ? (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                                <FaQuestionCircle className="text-red-500 text-2xl mx-auto mb-2" />
                                <p className="text-red-600 font-medium">
                                    No subjects assigned to your account
                                </p>
                                <p className="text-red-500 text-sm">
                                    Contact your admin/teacher for assistance
                                </p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {userSubjects.map((subject, index) => {
                                    const hasQuestions = subjectQuestionCount[subject] > 0;
                                    const isSelected = selectedSubjects.includes(subject);

                                    return (
                                        <button
                                            key={subject}
                                            disabled={!hasQuestions}
                                            onClick={() => hasQuestions && handleSubjectToggle(subject)}
                                            className={`relative p-4 border-2 rounded-lg text-left transition-all duration-200
        ${!hasQuestions
                                                    ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                                                    : isSelected
                                                        ? "border-green-500 bg-green-50 shadow-md"
                                                        : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                                                }
      `}
                                        >
                                            <div className="flex items-start gap-3">
                                                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold
          ${!hasQuestions
                                                        ? "bg-gray-300 text-gray-500"
                                                        : isSelected
                                                            ? "bg-green-500 text-white"
                                                            : "bg-gray-200 text-gray-400"
                                                    }
        `}>
                                                    {index + 1}
                                                </div>

                                                <div className="flex-1">
                                                    <span className="font-medium">{subject}</span>

                                                    {!hasQuestions && (
                                                        <p className="text-xs text-red-500 mt-1">
                                                            No questions available
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {selectedSubjects.length > 0 && (
                            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 text-green-700 mb-2">
                                    <FaCheckCircle />
                                    <span className="font-medium">
                                        Selected Subjects ({selectedSubjects.length}/{maxSubjects})
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {selectedSubjects.map((subject, idx) => (
                                        <span key={idx} className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                                            <span className="bg-green-500 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs">
                                                {idx + 1}
                                            </span>
                                            {subject}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Start Button */}
                    <div className="text-center">
                        <button
                            onClick={startExam}
                            disabled={selectedSubjects.length === 0}
                            className={`px-8 py-4 rounded-xl font-semibold text-lg shadow-lg transition-all duration-200 flex items-center gap-3 mx-auto ${selectedSubjects.length > 0
                                ? `${examInfo.color.replace('text-', 'bg-')} hover:shadow-xl text-white transform hover:scale-105`
                                : "bg-gray-400 text-gray-200 cursor-not-allowed"
                                }`}
                        >
                            <FaPlay className="text-lg" />
                            Start {examType.toUpperCase()} Examination
                        </button>

                        {selectedSubjects.length > 0 ? (
                            <p className="text-sm text-gray-500 mt-3">
                                {/* Ready to begin with {getFilteredQuestions().length} questions */}
                            </p>
                        ) : (

                            selectedSubjects.length === 0 && (
                                <p className="text-sm text-red-500 mt-3">
                                    Please select at least 1 subject to continue
                                </p>
                            )


                        )}
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="text-center text-gray-300 py-4 bg-[#2148C0] text-sm">
                <p className="flex items-center justify-center gap-2">
                    <FaGraduationCap />
                    &copy; {new Date().getFullYear()} TeeChaa CBT Application. All rights reserved.
                </p>
            </footer>
        </div>
    );
};

export default TakeJAMBOrWAECSession;